// Phase 5++ - Walk a metes-and-bounds description into a polygon.
//
// Input: structured calls parsed from text (via Claude) + a reference feature
//        that provides the commencement anchor (usually the containing section).
// Output: GeoJSON Polygon feature tracing the tract.

import { destination as turfDestination, bbox as turfBbox, distance as turfDistance } from '@turf/turf';

// Compute a named corner of a bounding box.
export function cornerOfBbox(bb, anchor) {
  if (!bb || bb.some((v) => !isFinite(v))) return null;
  const [x0, y0, x1, y1] = bb;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  switch ((anchor || '').toLowerCase().replace(/[_\s-]+/g, '')) {
    case 'necorner':
    case 'northeastcorner': return [x1, y1];
    case 'nwcorner':
    case 'northwestcorner': return [x0, y1];
    case 'secorner':
    case 'southeastcorner': return [x1, y0];
    case 'swcorner':
    case 'southwestcorner': return [x0, y0];
    case 'center':
    case 'centerofsection': return [mx, my];
    case 'nmidpoint':
    case 'northmidpoint':   return [mx, y1];
    case 'smidpoint':
    case 'southmidpoint':   return [mx, y0];
    case 'emidpoint':
    case 'eastmidpoint':    return [x1, my];
    case 'wmidpoint':
    case 'westmidpoint':    return [x0, my];
    default: return null;
  }
}

// Backwards-compat wrapper: compute a corner of a reference feature's bbox.
export function cornerOfFeature(feature, anchor) {
  return cornerOfBbox(turfBbox(feature), anchor);
}

// Parse surveyor-style quadrant bearing to compass degrees (0=N, clockwise).
// Accepts formats like "N 45° 30' 00\" E", "N45-30-00E", "North 45 deg 30 min East",
// "due East", "N 90 E" (= East), etc. Returns null if not parseable.
export function bearingToCompassDegrees(str) {
  if (str == null) return null;
  let s = String(str).toUpperCase().trim();
  s = s.replace(/DUE\s+NORTH/g, 'N 0 E').replace(/DUE\s+SOUTH/g, 'S 0 E')
       .replace(/DUE\s+EAST/g,  'N 90 E').replace(/DUE\s+WEST/g,  'N 90 W');
  s = s.replace(/NORTHEAST/g, 'NE').replace(/NORTHWEST/g, 'NW')
       .replace(/SOUTHEAST/g, 'SE').replace(/SOUTHWEST/g, 'SW');
  s = s.replace(/\bNORTH\b/g, 'N').replace(/\bSOUTH\b/g, 'S')
       .replace(/\bEAST\b/g, 'E').replace(/\bWEST\b/g, 'W');
  s = s.replace(/DEGREES?/g, '°').replace(/DEG\b/g, '°')
       .replace(/MINUTES?/g, "'").replace(/MIN\b/g, "'")
       .replace(/SECONDS?/g, '"').replace(/SEC\b/g, '"')
       .replace(/[°'"]+/g, ' ').replace(/[,-]+/g, ' ')
       .replace(/\s+/g, ' ').trim();
  // Accept both "N 45 30 00 E" and "N45-30-00E" (no-space) formats
  // After normalization above, the NS / EW letters may be adjacent to digits.
  const m = s.match(/^([NS])\s*(\d+(?:\.\d+)?)(?:\s+(\d+(?:\.\d+)?))?(?:\s+(\d+(?:\.\d+)?))?\s*([EW])$/);
  if (!m) {
    // Handle pure cardinals: "N", "S", "E", "W" alone
    const c = s.trim();
    if (c === 'N') return 0;
    if (c === 'S') return 180;
    if (c === 'E') return 90;
    if (c === 'W') return 270;
    return null;
  }
  const [, ns, dStr, mStr, sStr, ew] = m;
  const deg = parseFloat(dStr) + (parseFloat(mStr || 0) / 60) + (parseFloat(sStr || 0) / 3600);
  // Convert quadrant to compass
  if (ns === 'N' && ew === 'E') return deg;
  if (ns === 'S' && ew === 'E') return 180 - deg;
  if (ns === 'S' && ew === 'W') return 180 + deg;
  if (ns === 'N' && ew === 'W') return 360 - deg;
  return null;
}

// Given a structured metes parse and a reference feature (e.g. the section),
// return a Polygon feature tracing the tract. Returns null if the walk fails.
// Schema of `parsed`:
//   {
//     commencement: { anchor: "SE_corner", of: "section" } | null,
//     commencement_offsets: [{ bearing: "N 45° E", distance_ft: 100 }, ...],
//     calls:                [{ bearing: "N 45° E", distance_ft: 100 }, ...],
//   }
// If commencement is null we assume calls already start from the POB, and the
// POB is set to the centroid of the reference feature (best guess).
export function walkMetesAndBounds(parsed, referenceFeature, startCoord) {
  if (!parsed || !Array.isArray(parsed.calls) || parsed.calls.length < 3) return null;
  let cursor = startCoord || null;
  if (!cursor && parsed.commencement && parsed.commencement.anchor && referenceFeature) {
    cursor = cornerOfFeature(referenceFeature, parsed.commencement.anchor);
  }
  if (!cursor && referenceFeature) {
    const bb = turfBbox(referenceFeature);
    if (bb) cursor = [(bb[0] + bb[2]) / 2, (bb[1] + bb[3]) / 2];
  }
  if (!cursor) return null;
  // Apply commencement offsets to reach the Point of Beginning
  for (const off of (parsed.commencement_offsets || [])) {
    const next = advance(cursor, off);
    if (!next) return null;
    cursor = next;
  }
  const pob = cursor;
  const ring = [pob];
  for (const call of parsed.calls) {
    const next = advance(cursor, call);
    if (!next) return null;
    ring.push(next);
    cursor = next;
  }
  // Closure error: distance from the last walked vertex back to the POB
  // (before we force-close the ring). Also compute perimeter for the ratio.
  const last = ring[ring.length - 1];
  let closureFeet = 0;
  try { closureFeet = turfDistance(last, pob, { units: 'feet' }); } catch { closureFeet = 0; }
  let perimeterFeet = 0;
  for (const call of parsed.calls) {
    const d = Number(call.distance_ft);
    if (Number.isFinite(d) && d > 0) perimeterFeet += d;
  }
  const closureRatio = closureFeet > 0 && perimeterFeet > 0
    ? Math.round(perimeterFeet / closureFeet)
    : null;
  const closureQuality = closureRatio == null ? 'exact'
    : closureRatio >= 10000 ? 'excellent'
    : closureRatio >= 5000  ? 'good'
    : closureRatio >= 2000  ? 'fair'
    : 'poor';

  // Force-close the ring to avoid a visually open polygon even when closure
  // is non-zero. The closure error is still reported in properties.
  if (closureFeet > 0.01) ring.push(pob);

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {
      kind: 'metes_walk',
      closureFeet: +closureFeet.toFixed(2),
      perimeterFeet: Math.round(perimeterFeet),
      closureRatio,       // e.g. 15000 meaning 1 foot error per 15,000 feet
      closureQuality,     // 'excellent' | 'good' | 'fair' | 'poor' | 'exact'
    },
  };
}

function advance(from, call) {
  const deg = bearingToCompassDegrees(call.bearing);
  const dist = Number(call.distance_ft);
  if (deg == null || !isFinite(dist) || dist <= 0) return null;
  // turf.destination wants bearing in -180..180 from North, clockwise positive
  const normalized = deg > 180 ? deg - 360 : deg;
  const out = turfDestination(from, dist, normalized, { units: 'feet' });
  return out.geometry.coordinates;
}
